import api from '../api.service';

// The password itself lives on the API. We only forward what the admin typed into
// the prompt, so nothing secret ends up in the client bundle.
const DANGER_PASSWORD_HEADER = 'x-mx-admin-danger';

function dangerHeaders(dangerPassword) {
    return { headers: { [DANGER_PASSWORD_HEADER]: dangerPassword || '' } };
}

export async function johnDbOrgExtract() {
    return api
        .get(`/mx-admin/john/db/org/extract`)
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}
/**
 * @param orgId {String}
 * @param dangerPassword {String}
 * @param [expiresAt] {String} 'YYYY-MM-DD', the last licensed day. Empty = forever.
 */
export async function licenseOrg(orgId, dangerPassword, expiresAt) {
    return api
        .post(`/mx-admin/org/license/${orgId}`, { expiresAt }, dangerHeaders(dangerPassword))
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function setOrgExtraFreeSeats(orgId, extraFreeSeats) {
    return api
        .put("/mx-admin/org/seats/free", {orgId, extraFreeSeats})
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function setOrgPeopleLimit(orgId, peopleLimit) {
    if (typeof peopleLimit === 'undefined') {
        throw Error('Please set a number for people limit. Set null or zero to unset.');
    }

    const url = peopleLimit === null || peopleLimit === 0 ? '/mx-admin/org/limit/people/unset' : '/mx-admin/org/limit/people'

    return api
        .put(url, {orgId, peopleLimit})
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function deleteAllOrgMissions(orgId, dangerPassword) {
    return api
        .post('/mx-admin/org/delete/missions/all', {orgId}, dangerHeaders(dangerPassword))
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function permanentlyDeleteUser(userId, dangerPassword) {
    return api
        .delete(`/mx-admin/user/${userId}`, dangerHeaders(dangerPassword))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function getAppSumoActivationLink(email) {
    return api
        .post(`/mx-admin/appsumo/activation/link`, {email})
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function gtResetLoginProvider(email) {
    return api
        .post(`/mx-admin/gt/auth/reset-login-provider`, {email})
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function setMitsiMode(on, userIdOrEmail) {
    return api
        .post(`/mx-admin/mitsiMode`, {on, mitsi: userIdOrEmail})
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function getMitsiMode() {
    return api
        .get(`/mx-admin/mitsiMode`)
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function getOrgPersonContractInfo(orgId, personEmail) {
    return api
        .get(
            `/mx-admin/org-person/contract-info?orgId=${encodeURIComponent(orgId)}&personEmail=${encodeURIComponent(
                personEmail,
            )}`,
        )
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function addOrgPersonContractHistory(payload) {
    return api
        .post(`/mx-admin/org-person/contract-history`, payload)
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function removeOrgPersonContractHistory(payload) {
    return api
        .post(`/mx-admin/org-person/contract-history/remove`, payload)
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Marks one of a person's trials (TRIAL_PRODUCT) as used or available again.
 * @param email {String}
 * @param product {String} One of TRIAL_PRODUCT
 * @param used {Boolean}
 */
export async function setPersonTrialUsed(email, product, used) {
    return api
        .put(`/mx-admin/person/trial-used`, { email, product, used })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function setOrgIsVerified(orgId, isVerified) {
    return api
        .put(`/mx-admin/org/is-verified`, { orgId, isVerified })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function setOrgPlan(orgId, plan) {
    return api
        .put(`/mx-admin/org/plan`, { orgId, plan })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Sets, extends or clears the end date of an Enterprise license.
 * @param orgId {String}
 * @param expiresAt {String|null} 'YYYY-MM-DD', the last licensed day. Empty = forever.
 */
export async function setOrgEnterpriseExpiry(orgId, expiresAt) {
    return api
        .put(`/mx-admin/org/enterprise/expiry`, { orgId, expiresAt })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export async function resetOrgPlan(orgId) {
    return api
        .put(`/mx-admin/org/plan/reset`, { orgId })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Marks one of an org's trials (TRIAL_PRODUCT) as used or available again. A trial is
 * one per org AND one per person, so re-running one needs setPersonTrialUsed too.
 * @param orgId {String}
 * @param product {String} One of TRIAL_PRODUCT
 * @param used {Boolean}
 */
export async function setOrgTrialUsed(orgId, product, used) {
    return api
        .put(`/mx-admin/org/trial-used`, { orgId, product, used })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Grants an add-on tier to an org outside Stripe, or revokes it with a null tier. For
 * Enterprise customers invoiced directly, or to keep an org going after a trial by hand.
 * @param orgId {String}
 * @param product {String} One of BILLING_PRODUCT
 * @param tier {String|null} A tier id of that product, or null to revoke
 */
export async function setOrgAddonManualTier(orgId, product, tier) {
    return api
        .put(`/mx-admin/org/addon/manual-tier`, { orgId, product, tier })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * One page of MxCrm's grid: org admins, each carrying the orgs they created so the
 * licensing/add-on columns can be resolved with the same helpers the billing pages use
 * (utils/org-plan.js, utils/addon-plan.js).
 *
 * @param options {Object}
 * @param options.skip {Number} The page's first row.
 * @param options.limit {Number} Page size.
 * @param options.sort {String} createdAt | email | name
 * @param options.dir {String} asc | desc
 * @param options.search {String} Matches email, first or last name.
 * @returns {Promise<{total: Number, skip: Number, limit: Number, people: Object[]}>}
 */
export async function getCrmPeople({ skip, limit, sort, dir, search } = {}) {
    const q = new URLSearchParams()

    if (skip) q.set("skip", skip)
    if (limit) q.set("limit", limit)
    if (sort) q.set("sort", sort)
    if (dir) q.set("dir", dir)
    if (search) q.set("search", search)

    return api
        .get(`/mx-admin/crm/people${q.toString() ? `?${q}` : ""}`)
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * MxCrm's profile card. One person, their created orgs, and `payingUsage` on each org -
 * the billed headcount per tier, which is what the spend figure is multiplied out from.
 *
 * @param personId {String}
 */
export async function getCrmPerson(personId) {
    return api
        .get(`/mx-admin/crm/person/${personId}`)
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Gives one person free AI credits by hand (included, no expiry). Behind the danger password.
 *
 * @param email {String}
 * @param orgId {String|null} Needed only when the person is in more than one org
 * @param credits {Number} MAX_AI_ADMIN_GIFT.min to max
 * @param dangerPassword {String}
 * @return {Promise<{email, name, orgId, orgTitle, credits, available}>}
 */
export async function giftMaxCredits(email, orgId, credits, dangerPassword) {
    return api
        .post(
            `/mx-admin/max-credits/gift`,
            { email, orgId: orgId || undefined, credits },
            dangerHeaders(dangerPassword),
        )
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}
